import { apiClient, handleApiError, EXPORT_REQUEST_TIMEOUT } from './client';
import axios, { AxiosError } from 'axios';
import { getWeekDates as getTrainingWeekDates, findTrainingYearForDate, TRAINING_START_BY_YEAR } from '@/utils/dateUtils';
import {
  LoginData,
  RegisterData,
  LogoutData,
  RefreshData,
  UserProfile,
  UpdateProfileData,
  DashboardData,
  Company,
  CreateCompanyData,
  DailyReport,
  CreateDailyReportData,
  WeeklyReport,
  CreateWeeklyReportData,
  GenerateWeeklyData,
  MainJobOperation,

  GeneralReport,
  EnhanceTextData,
  EnhanceTextResponse,
  EnhanceDailyData,
  EnhanceWeeklyData,
  EnhanceGeneralData,
  GenerateSummaryData,
  GenerateSummaryResponse,
  SuggestImprovementsData,
  AIUsageStats,
  BulkExportData,
  MainJob,
  CreateMainJobData,
  CreateMainJobOperationData,
  UserBalance,
  Transaction,
  Notification,
  CreateTransactionData,
  TokenUsage,
  BillingDashboardData,
  PaymentInfo,
  ReportResource,
} from '@/types';

// Authentication Services
export const authService = {
  login: async (data: LoginData) => {
    try {
      // Ensure the request format matches the backend expectations
      const loginData = {
        username: data.username,
        password: data.password
      };
      
      console.log('🔐 Sending login request:', loginData);
      console.log('🔐 Login endpoint:', '/auth/login/');
      console.log('🔐 Request headers:', {
        'Content-Type': 'application/json'
      });
      
      const response = await apiClient.post('/auth/login/', loginData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Login response:', response.data);
      console.log('✅ Response status:', response.status);
      console.log('✅ Response headers:', response.headers);
      return response.data;
    } catch (error: any) {
      console.error('❌ Login error:', error);
      console.error('❌ Response data:', error.response?.data);
      console.error('❌ Response status:', error.response?.status);
      console.error('❌ Response headers:', error.response?.headers);
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  // Check if phone number is already registered
  checkPhoneNumberAvailability: async (phoneNumber: string): Promise<{ available: boolean; message?: string }> => {
    try {
      console.log('📱 Checking phone number availability:', phoneNumber);
      
      const response = await apiClient.get('/auth/check-phone/', {
        params: { phone_number: phoneNumber }
      });
      
      console.log('✅ Phone number check response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Phone number check error:', error);
      
      // Handle different error scenarios
      if (error.response?.status === 404) {
        console.log('⚠️ Phone number check endpoint not implemented yet, assuming available');
        return { 
          available: true, 
          message: 'Phone number validation not available yet - proceeding with registration' 
        };
      } else if (error.response?.status === 400) {
        console.log('⚠️ Invalid phone number format');
        return { 
          available: false, 
          message: 'Invalid phone number format. Please use a valid Tanzanian phone number.' 
        };
      } else if (error.response?.status === 500) {
        console.log('⚠️ Backend server error during phone validation');
        return { 
          available: true, 
          message: 'Phone validation service temporarily unavailable - proceeding with registration' 
        };
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        console.log('⚠️ Network error during phone validation');
        return { 
          available: true, 
          message: 'Network error during phone validation - proceeding with registration' 
        };
      }
      
      // For any other errors, assume available to not block registration
      console.log('⚠️ Unexpected error during phone validation, assuming available');
      return { 
        available: true, 
        message: 'Phone validation error - proceeding with registration' 
      };
    }
  },

  // Send OTP to phone number
  sendOTP: async (phone: string, purpose: string): Promise<{ success: boolean; message: string }> => {
    try {
      console.log('📱 Sending OTP to:', phone, 'for purpose:', purpose);
      const response = await apiClient.post('/auth/send-otp/', {
        phone,
        purpose
      });
      console.log('✅ OTP sent successfully:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to send OTP:', error);
      console.error('❌ Response data:', error.response?.data);
      console.error('❌ Response status:', error.response?.status);
      throw error;
    }
  },

  // Verify OTP code
  verifyOTP: async (phone: string, code: string, purpose: string): Promise<{ success: boolean; message: string }> => {
    try {
      console.log('🔐 Verifying OTP for:', phone, 'with code:', code);
      const response = await apiClient.post('/auth/verify-otp/', {
        phone,
        code,
        purpose
      });
      console.log('✅ OTP verified successfully:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to verify OTP:', error);
      console.error('❌ Response data:', error.response?.data);
      console.error('❌ Response status:', error.response?.status);
      throw error;
    }
  },

  // Request password reset (backend may use either endpoint)
  requestPasswordReset: async (email: string): Promise<{ detail?: string; message?: string }> => {
    try {
      const frontendUrl = (import.meta as any)?.env?.VITE_FRONTEND_URL || (typeof window !== 'undefined' ? window.location.origin : '');
      const payload = { email, frontend_url: frontendUrl } as const;
      const response = await apiClient.post('/auth/password-reset/', payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response.data;
    } catch (error: any) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  // Confirm password reset (expects uid, token, and password)
  confirmPasswordResetWithUid: async (data: { uid: string; token: string; password: string }): Promise<{ message?: string }> => {
    try {
      const form = new URLSearchParams();
      form.append('uid', data.uid);
      form.append('token', data.token);
      form.append('password', data.password);
      const response = await apiClient.post('/auth/password-reset-confirm/', form, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      return response.data;
    } catch (error: any) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  register: async (data: RegisterData) => {
    try {
      console.log('📝 Sending registration request:', {
        username: data.username,
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        phone_number: data.phone_number,
        student_id: data.student_id,
        program: data.program,
        pt_phase: data.pt_phase,
        year_of_study: data.year_of_study,
        department: data.department,
        supervisor_name: data.supervisor_name,
        supervisor_email: data.supervisor_email,
        company_name: data.company_name,
        company_region: data.company_region,
        password: '[HIDDEN]',
        password_confirm: '[HIDDEN]'
      });
      
      // Log the exact JSON being sent
      // Enrich registration with fingerprint/device identifiers to support backend risk checks
      const registrationData: any = {
        username: data.username,
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        password: data.password,
        password_confirm: data.password_confirm,
        phone_number: data.phone_number,
        student_id: data.student_id,
        program: data.program,
        year_of_study: data.year_of_study,
        pt_phase: data.pt_phase,
        department: data.department,
        supervisor_name: data.supervisor_name,
        supervisor_email: data.supervisor_email,
        company_name: data.company_name,
        company_region: data.company_region
      };

      // Best-effort include fingerprint and device info payload (in addition to headers set in client)
      try {
        const [{ getFingerprint }] = await Promise.all([
          import('@/utils/fingerprint') as any,
        ]);
        const fp = await getFingerprint();
        if (fp?.visitorId) {
          registrationData.device_fingerprint = fp.visitorId;
          if (typeof fp.confidence === 'number') {
            registrationData.device_fingerprint_confidence = fp.confidence;
          }
        }
      } catch {}
      try {
        const { collectDeviceInfo } = await import('@/utils/deviceInfo');
        const info = collectDeviceInfo();
        registrationData.device_info = info;
      } catch {}
      
      console.log('📤 Exact JSON being sent to backend:', JSON.stringify(registrationData, null, 2));
      
      const response = await apiClient.post('/auth/register/', registrationData);
      console.log('✅ Registration response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Registration error:', error);
      console.error('❌ Response data:', error.response?.data);
      console.error('❌ Response status:', error.response?.status);
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  logout: async (data: LogoutData) => {
    try {
      const response = await apiClient.post('/auth/logout/', data);
      return response.data;
    } catch (error: any) {
      // Logout should always succeed, even if token is expired
      console.log('⚠️ Logout API call failed (token may be expired), but proceeding with local cleanup');
      // Don't throw error - logout should always work
      return { success: true, message: 'Logged out successfully' };
    }
  },

  refreshToken: async (data: RefreshData) => {
    try {
      const response = await apiClient.post('/auth/refresh/', data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  // Google Authentication
  googleAuth: async (idToken: string): Promise<{ success: boolean; message: string; data: any }> => {
    try {
      console.log('Sending Google token, length:', idToken.length);
      // Send as query parameter to bypass body truncation
      const response = await apiClient.post('/auth/google-auth/', null, {
        params: { id_token: idToken }
      });
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  googleCompleteRegistration: async (data: any): Promise<{ success: boolean; message: string; data: any }> => {
    try {
      const response = await apiClient.post('/auth/google-complete-registration/', data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },
};

// Profile Services
export const profileService = {
  getProfile: async (): Promise<UserProfile> => {
    try {
      const response = await apiClient.get('/auth/profile/');
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  verifyEmailOtp: async (data: { email: string; otp: string; admin_code?: string }): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.post('/auth/verify-email-otp/', data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  sendEmailOtp: async (email: string): Promise<{ success?: boolean; message?: string }> => {
    try {
      const response = await apiClient.post('/auth/send-email-otp/', { email });
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  updateProfile: async (data: UpdateProfileData): Promise<UserProfile> => {
    try {
      const response = await apiClient.put('/auth/profile/', data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  uploadProfilePicture: async (file: File): Promise<{ success: boolean; message: string; profile_picture_url: string }> => {
    try {
      console.log('📤 API: Starting profile picture upload');
      console.log('📤 API: File details:', {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified
      });
      
      const formData = new FormData();
      formData.append('profile_picture', file);
      
      // Additional debugging - try different field names to test
      console.log('📤 API: Testing FormData creation...');
      const testFormData = new FormData();
      testFormData.append('profile_picture', file);
      testFormData.append('test_field', 'test_value');
      
      console.log('📤 API: Test FormData entries:');
      for (let [key, value] of testFormData.entries()) {
        console.log(`📤 API: Test - ${key}:`, value);
      }
      
      // Log FormData contents for debugging
      console.log('📤 API: FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`📤 API: ${key}:`, value);
        if (value instanceof File) {
          console.log(`📤 API: File details for ${key}:`, {
            name: value.name,
            size: value.size,
            type: value.type,
            lastModified: value.lastModified
          });
        }
      }
      
      // Verify FormData has the expected field
      const hasProfilePicture = formData.has('profile_picture');
      console.log('📤 API: FormData has "profile_picture" field:', hasProfilePicture);
      
      // Use the correct endpoint structure based on backend
      console.log('📤 API: Making request to /auth/profile/upload-picture/');
      console.log('📤 API: Request config:', {
        method: 'POST',
        url: '/auth/profile/upload-picture/',
        hasAuthToken: !!localStorage.getItem('access_token'),
        formDataSize: formData.has('profile_picture') ? 'Has profile_picture field' : 'Missing profile_picture field'
      });
      
      // Create a simple axios instance for file upload to avoid interference from interceptors
      const token = localStorage.getItem('access_token');
      const baseURL = apiClient.defaults.baseURL;
      
      console.log('📤 API: Using direct axios call with minimal headers');
      const response = await axios.post(`${baseURL}/auth/profile/upload-picture/`, formData, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : undefined,
          // Let browser set Content-Type with proper boundary
        },
        timeout: 30000,
      });
      
      console.log('📤 API: Upload successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('📤 API: Upload failed:', error);
      
      // Enhanced error logging
      if (error instanceof Error) {
        console.error('📤 API: Error details:', {
          message: error.message,
          name: error.name,
          stack: error.stack
        });
      }
      
      if ((error as any)?.response) {
        console.error('📤 API: Response error:', {
          status: (error as any).response.status,
          statusText: (error as any).response.statusText,
          data: (error as any).response.data,
          headers: (error as any).response.headers
        });
        
        // Log the specific error message from backend
        if ((error as any).response.data) {
          console.error('📤 API: Backend error details:', {
            error: (error as any).response.data.error,
            message: (error as any).response.data.message,
            success: (error as any).response.data.success,
            fullResponse: (error as any).response.data
          });
          
          // Check if it's the "no picture provided" error specifically
          const errorMsg = (error as any).response.data.error || (error as any).response.data.message || '';
          if (errorMsg.toLowerCase().includes('no') && errorMsg.toLowerCase().includes('picture')) {
            console.error('🚨 SPECIFIC ISSUE: Backend says "no picture provided"');
            console.error('🚨 This means FormData is not being received correctly by backend');
            console.error('🚨 Check if field name matches backend expectation');
          }
        }
      }
      
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  removeProfilePicture: async (): Promise<{ success: boolean; message: string }> => {
    try {
      // Use the correct endpoint structure based on backend
      const response = await apiClient.delete('/auth/profile/remove-picture/');
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  resendVerificationEmail: async (email: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.post('/auth/resend-verification/', { email });
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },
};

// Dashboard Services
// NOTE: Main dashboard endpoint /auth/dashboard/ is temporarily disabled due to backend serializer error:
// "The field 'profile_picture' was declared on serializer UserSerializer, but has not been included in the 'fields' option."
export const dashboardService = {
  getDashboard: async (): Promise<DashboardData> => {
    try {
      console.log('🔍 Attempting to fetch dashboard from multiple endpoints...');
      
      // Try the main dashboard endpoint first (TEMPORARILY DISABLED due to backend serializer error)
      try {
        console.log('🔍 Main dashboard endpoint /auth/dashboard/ is temporarily disabled due to backend serializer error');
        console.log('🔍 Skipping to alternative endpoints...');
        throw new Error('Main dashboard endpoint temporarily disabled');
      } catch (mainError: any) {
        console.warn('⚠️ Main dashboard endpoint skipped:', mainError.message);
        
        // Try alternative dashboard endpoints
        try {
          console.log('🔍 Trying /billing/dashboard/dashboard_data/...');
          const billingResponse = await apiClient.get('/billing/dashboard/dashboard_data/');
          console.log('✅ Billing dashboard response:', billingResponse.data);
          
          // Transform billing data to dashboard format if possible
          if (billingResponse.data?.data) {
            return {
              user: null, // Will be filled by profile service
              stats: {
                daily_reports: 0, // Default values
                weekly_reports: 0,
                submitted_weekly_reports: 0,
                general_report_status: 'pending'
              },
              profile_complete: false
            };
          }
        } catch (billingError: any) {
          console.warn('⚠️ Billing dashboard endpoint also failed:', billingError.response?.status);
        }
        
        // If all endpoints fail, provide fallback data instead of throwing error
        console.warn('⚠️ All dashboard endpoints failed, providing fallback data');
        return {
          user: null, // Will be filled by profile service
          stats: {
            daily_reports: 0,
            weekly_reports: 0,
            submitted_weekly_reports: 0,
            general_report_status: 'pending'
          },
          profile_complete: false
        };
      }
    } catch (error: any) {
      console.error('❌ All dashboard endpoints failed');
      console.error('❌ Final error status:', error.response?.status);
      console.error('❌ Final error data:', error.response?.data);
      throw error;
    }
  },
};

// Company Services
export const companyService = {
  getCompanies: async (): Promise<Company[]> => {
    try {
      const response = await apiClient.get('/companies/');
      return response.data.results || response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  createCompany: async (data: CreateCompanyData): Promise<Company> => {
    try {
      const response = await apiClient.post('/companies/', data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  searchCompanies: async (query: string): Promise<Company[]> => {
    try {
      const response = await apiClient.get(`/companies/search/?q=${encodeURIComponent(query)}`);
      return response.data.results || response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  getCompanyStudents: async (companyId: number): Promise<any[]> => {
    try {
      const response = await apiClient.get(`/companies/${companyId}/students/`);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },
};

// Daily Reports Services
export const dailyReportService = {
  getDailyReports: async (): Promise<DailyReport[]> => {
    try {
      const response = await apiClient.get('/reports/daily/');
      return response.data.results || response.data;
    } catch (error) {
      console.error('Failed to fetch daily reports:', error);
      throw error;
    }
  },

  createDailyReport: async (data: CreateDailyReportData): Promise<DailyReport> => {
    try {
      // Map frontend data to backend API format
      const backendData = {
        date: data.date || new Date().toISOString().split('T')[0], // Use the actual date
        description: data.description || '', // Ensure description is provided
        hours_spent: data.hours_spent || 0, // Keep as hours_spent for backend
        week_number: data.week_number
      };
      
      console.log('Backend data being sent:', backendData);
      
      const response = await apiClient.post('/reports/daily/', backendData);
      console.log('Backend response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Backend error details:', (error as any).response?.data);
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  updateDailyReport: async (id: number, data: Partial<CreateDailyReportData>): Promise<DailyReport> => {
    try {
      // Map frontend data to backend API format
      const backendData: any = {};
      if (data.date) {
        backendData.date = data.date; // Use the actual date
      }
      if (data.description) backendData.description = data.description;
      if (data.hours_spent !== undefined) backendData.hours_spent = data.hours_spent;
      if (data.week_number) backendData.week_number = data.week_number;
      
      const response = await apiClient.put(`/reports/daily/${id}/`, backendData);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  submitDailyReport: async (id: number): Promise<DailyReport> => {
    try {
      const response = await apiClient.patch(`/reports/daily/${id}/submit/`);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  getWeeklyDailyReports: async (weekNumber: number): Promise<DailyReport[]> => {
    try {
      const response = await apiClient.get(`/reports/daily/week/?week_number=${weekNumber}`);
      return response.data.results || response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },
};

// Weekly Reports Services
export const weeklyReportService = {
  getWeeklyReports: async (): Promise<WeeklyReport[]> => {
    try {
      const response = await apiClient.get('/reports/weekly/');
      return response.data.results || response.data;
    } catch (error) {
      console.error('Failed to fetch weekly reports:', error);
      throw error;
    }
  },

  createWeeklyReport: async (data: CreateWeeklyReportData): Promise<WeeklyReport> => {
    try {
      const response = await apiClient.post('/reports/weekly/', data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  updateWeeklyReport: async (weekNumber: number, data: Partial<CreateWeeklyReportData>): Promise<WeeklyReport> => {
    try {
      const url = `/reports/weekly/week/${weekNumber}/`;
      console.log('Updating weekly report at URL:', url);
      console.log('Data being sent:', data);
      const response = await apiClient.put(url, data);
      return response.data;
    } catch (error) {
      console.error('Failed to update weekly report:', error);
      throw error;
    }
  },

  getWeeklyReportByWeek: async (weekNumber: number): Promise<WeeklyReport> => {
    try {
      const url = `/reports/weekly/week/${weekNumber}/`;
      console.log('Fetching weekly report at URL:', url);
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch weekly report:', error);
      throw error;
    }
  },

  getDailyReportsByWeek: async (weekNumber: number): Promise<DailyReport[]> => {
    try {
      const response = await apiClient.get(`/reports/daily/?week_number=${weekNumber}`);
      return response.data.results || response.data;
    } catch (error) {
      console.error('Failed to fetch daily reports:', error);
      throw error;
    }
  },

  createDailyReport: async (data: any): Promise<DailyReport> => {
    try {
      const response = await apiClient.post('/reports/daily/', data);
      return response.data;
    } catch (error) {
      console.error('Failed to create daily report:', error);
      throw error;
    }
  },



  generateWeeklyReport: async (data: GenerateWeeklyData): Promise<WeeklyReport> => {
    try {
      const response = await apiClient.post('/reports/weekly/generate/', data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  submitWeeklyReport: async (weekNumber: number): Promise<WeeklyReport> => {
    try {
      const response = await apiClient.patch(`/reports/weekly/week/${weekNumber}/submit/`);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  getWeeklyReport: async (id: number): Promise<WeeklyReport> => {
    try {
      const response = await apiClient.get(`/reports/weekly/week/${id}/`);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  downloadWeeklyReportPDF: async (weekNumber: number): Promise<Blob> => {
    try {
      const response = await apiClient.get(`/reports/weekly/week/${weekNumber}/download/pdf/`, {
        responseType: 'blob',
        timeout: EXPORT_REQUEST_TIMEOUT,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to download PDF:', error);
      throw error;
    }
  },

  downloadWeeklyReportDOCX: async (weekNumber: number): Promise<Blob> => {
    try {
      const response = await apiClient.get(`/reports/weekly/week/${weekNumber}/download/docx/`, {
        responseType: 'blob',
        timeout: EXPORT_REQUEST_TIMEOUT,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to download DOCX:', error);
      throw error;
    }
  },

  downloadAllWeeklyReports: async (type: 'pdf' | 'docx', forceExport: boolean = false): Promise<Blob> => {
    try {
      const response = await apiClient.post('/export/all-weekly-reports/', {
        type: type,
        force_export: forceExport
      }, {
        responseType: 'blob',
        timeout: EXPORT_REQUEST_TIMEOUT,
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to download all reports:', error);
      // Check if it's a validation error (not a blob response)
      if (error.response && error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        let json: any = {};
        try {
          json = JSON.parse(text);
        } catch {
          json = {};
        }
        const parsedError: any = new Error(json.message || 'Failed to download all reports');
        parsedError.requiresTokens = Boolean(json.requires_tokens);
        parsedError.tokenCost = json.token_cost;
        parsedError.availableTokens = json.available_tokens;
        parsedError.tokenBreakdown = json.token_breakdown;
        throw parsedError;
      }
      throw error;
    }
  },

  resetWeeklyReport: async (reportId: number): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.post(`/reports/weekly/${reportId}/reset_to_original/`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to reset weekly report:', error);
      
      // Handle 404 specifically - reset endpoint not available
      if (error.response?.status === 404) {
        return {
          success: false,
          message: 'Reset is not available for this report'
        };
      }
      
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  revertWeeklyReport: async (
    weekNumber: number
  ): Promise<{ success: boolean; message: string; token_cost?: number; tokens_deducted?: number }> => {
    try {
      const response = await apiClient.post(`/reports/weekly/week/${weekNumber}/reset_to_original/`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to revert weekly report:', error);

      // Handle 404 specifically - revert endpoint not available
      if (error.response?.status === 404) {
        return {
          success: false,
          message: 'Revert is not available for this report'
        };
      }

      // Surface the backend's insufficient-token payload so the UI can show the
      // exact cost and remaining balance instead of a generic error.
      const data = error.response?.data;
      if (data && typeof data === 'object' && (data.requires_tokens || data.message)) {
        const parsedError: any = new Error(data.message || 'Failed to revert weekly report');
        parsedError.requiresTokens = Boolean(data.requires_tokens);
        parsedError.tokenCost = data.token_cost;
        parsedError.availableTokens = data.available_tokens;
        throw parsedError;
      }

      throw error;
    }
  },

  enhanceWithAI: async (weekNumber: number, additionalInstructions?: string): Promise<WeeklyReport> => {
    try {
      console.log('Sending AI enhancement request to backend...');
      console.log('Week Number:', weekNumber);
      console.log('Additional Instructions:', additionalInstructions || 'None');
      
      // First, check if the weekly report exists
      try {
        const reportCheck = await apiClient.get(`/reports/weekly/week/${weekNumber}/`);
        console.log('Weekly report exists:', reportCheck.data);
      } catch (checkError: any) {
        console.error('Weekly report check failed:', checkError.response?.status, checkError.response?.data);
        if (checkError.response?.status === 404) {
          throw new Error('Weekly report not found. Please check the report ID.');
        }
      }
      
      // Use the correct endpoint: /enhance_with_ai/ instead of /enhance/
      const response = await apiClient.post(`/reports/weekly/week/${weekNumber}/enhance_with_ai/`, {
        additional_instructions: additionalInstructions || ''
      });
      
      console.log('Backend AI enhancement successful:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Backend AI enhancement failed:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Check if it's an authentication error
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      
      // Check if it's a 404 error (weekly report not found)
      if (error.response?.status === 404) {
        throw new Error('Weekly report not found. Please check the report ID.');
      }
      
      // Check if it's a 500 error (server error)
      if (error.response?.status === 500) {
        console.warn('Server error (500), using mock enhancement for development');
        console.warn('Error details:', error.response?.data);
        
        // Mock enhancement for development/testing
        const trainingYear = findTrainingYearForDate(new Date()) ?? Math.max(...Object.keys(TRAINING_START_BY_YEAR).map(Number));
        const mockWeekDates = getTrainingWeekDates(weekNumber, trainingYear);
        const mockDailyDates = Array.from({ length: 5 }, (_, index) => {
          const date = new Date(mockWeekDates.startDate);
          date.setDate(mockWeekDates.startDate.getDate() + index);
          return date.toISOString().split('T')[0];
        });

        const mockEnhancedReport: WeeklyReport = {
          id: weekNumber,
          week_number: weekNumber,
          start_date: mockWeekDates.startDateStr,
          end_date: mockWeekDates.endDateStr,
          total_hours: 40,
          status: 'DRAFT',
          summary: 'Enhanced: Comprehensive weekly report with improved technical language',
          main_job_title: 'Enhanced: Sequence of Operations of Electrical System Design',
          main_job_description: 'Enhanced: Conducted comprehensive analysis and implementation of electrical system design principles with advanced technical methodologies.',
          daily_reports: [
            {
              id: 1,
              date: mockDailyDates[0],
              description: 'Enhanced: Conducted comprehensive analysis of electrical system requirements and design specifications',
              hours_spent: 8,
              week_number: weekNumber,
              is_submitted: false,
              is_editable: true,
              student_name: 'Student',
              skills_learned: 'Enhanced: Advanced electrical design principles and AutoCAD proficiency',
              challenges_faced: 'Enhanced: Complex system integration challenges requiring innovative problem-solving approaches',
              supervisor_feedback: 'Enhanced: Excellent technical understanding and professional approach to design challenges',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            },
            {
              id: 2,
              date: mockDailyDates[1],
              description: 'Enhanced: Implemented sophisticated electrical circuit design using industry-standard software tools',
              hours_spent: 8,
              week_number: weekNumber,
              is_submitted: false,
              is_editable: true,
              student_name: 'Student',
              skills_learned: 'Enhanced: Circuit design optimization and technical documentation',
              challenges_faced: 'Enhanced: Optimization of circuit efficiency while maintaining safety standards',
              supervisor_feedback: 'Enhanced: Outstanding attention to detail and technical accuracy',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            },
            {
              id: 3,
              date: mockDailyDates[2],
              description: 'Enhanced: Performed detailed system testing and validation procedures with professional documentation',
              hours_spent: 8,
              week_number: weekNumber,
              is_submitted: false,
              is_editable: true,
              student_name: 'Student',
              skills_learned: 'Enhanced: System testing methodologies and quality assurance protocols',
              challenges_faced: 'Enhanced: Ensuring comprehensive test coverage across multiple system components',
              supervisor_feedback: 'Enhanced: Thorough testing approach and excellent documentation skills',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            },
            {
              id: 4,
              date: mockDailyDates[3],
              description: 'Enhanced: Conducted advanced troubleshooting and system optimization with technical analysis',
              hours_spent: 8,
              week_number: weekNumber,
              is_submitted: false,
              is_editable: true,
              student_name: 'Student',
              skills_learned: 'Enhanced: Advanced troubleshooting techniques and system optimization',
              challenges_faced: 'Enhanced: Identifying and resolving complex system performance issues',
              supervisor_feedback: 'Enhanced: Excellent problem-solving skills and technical expertise',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            },
            {
              id: 5,
              date: mockDailyDates[4],
              description: 'Enhanced: Completed comprehensive project documentation and technical report preparation',
              hours_spent: 8,
              week_number: weekNumber,
              is_submitted: false,
              is_editable: true,
              student_name: 'Student',
              skills_learned: 'Enhanced: Technical writing and professional documentation standards',
              challenges_faced: 'Enhanced: Synthesizing complex technical information into clear documentation',
              supervisor_feedback: 'Enhanced: Exceptional documentation quality and professional presentation',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }
          ],
          main_job: {
            id: 1,
            title: 'Enhanced: Sequence of Operations of Electrical System Design',
            operations: [
              {
                id: 1,
                step_number: 1,
                operation_description: 'Enhanced: Conducted comprehensive analysis of electrical system requirements and design specifications using advanced engineering principles',
                tools_used: 'Enhanced: AutoCAD Electrical, MATLAB, Electrical design software, Technical documentation tools',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              },
              {
                id: 2,
                step_number: 2,
                operation_description: 'Enhanced: Implemented sophisticated electrical circuit design with optimization for efficiency and safety compliance',
                tools_used: 'Enhanced: Circuit design software, Simulation tools, Safety analysis software',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              },
              {
                id: 3,
                step_number: 3,
                operation_description: 'Enhanced: Performed detailed system testing and validation procedures with comprehensive quality assurance protocols',
                tools_used: 'Enhanced: Testing equipment, Quality assurance software, Documentation systems',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              },
              {
                id: 4,
                step_number: 4,
                operation_description: 'Enhanced: Conducted advanced troubleshooting and system optimization with technical analysis and performance evaluation',
                tools_used: 'Enhanced: Diagnostic tools, Performance monitoring software, Analysis tools',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              },
              {
                id: 5,
                step_number: 5,
                operation_description: 'Enhanced: Completed comprehensive project documentation and technical report preparation with professional standards',
                tools_used: 'Enhanced: Technical writing software, Documentation tools, Presentation software',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              }
            ]
          },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        // Add custom instructions if provided
        if (additionalInstructions) {
          mockEnhancedReport.summary += ` (Custom: ${additionalInstructions})`;
        }
        
        return mockEnhancedReport;
      }
      
      // For other errors, throw the original error
      throw error;
    }
  },
};

// Operations Services
export const operationService = {
  getOperations: async (mainJobId: number): Promise<MainJobOperation[]> => {
    try {
      const response = await apiClient.get(`/reports/main-jobs/${mainJobId}/operations/`);
      return response.data.results || response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  createOperation: async (mainJobId: number, data: any): Promise<MainJobOperation> => {
    try {
      const response = await apiClient.post(`/reports/main-jobs/${mainJobId}/operations/`, data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  updateOperation: async (mainJobId: number, operationId: number, data: any): Promise<MainJobOperation> => {
    try {
      const response = await apiClient.put(`/reports/main-jobs/${mainJobId}/operations/${operationId}/`, data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  deleteOperation: async (mainJobId: number, operationId: number): Promise<void> => {
    try {
      await apiClient.delete(`/reports/main-jobs/${mainJobId}/operations/${operationId}/`);
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },
};

// General Reports Services
export const generalReportService = {
  getGeneralReport: async (): Promise<GeneralReport> => {
    try {
      const response = await apiClient.get('/reports/general/');
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  generateGeneralReport: async (): Promise<GeneralReport> => {
    try {
      const response = await apiClient.post('/reports/general/generate/');
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  submitGeneralReport: async (): Promise<GeneralReport> => {
    try {
      const response = await apiClient.patch('/reports/general/submit/');
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },
};

// AI Services
export const aiService = {
  enhanceText: async (data: EnhanceTextData): Promise<EnhanceTextResponse> => {
    try {
      const response = await apiClient.post('/ai/enhance/text/', data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  enhanceDailyReport: async (data: EnhanceDailyData): Promise<EnhanceTextResponse> => {
    try {
      const response = await apiClient.post('/ai/enhance/daily/', data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  enhanceWeeklyReport: async (data: EnhanceWeeklyData): Promise<EnhanceTextResponse> => {
    try {
      const response = await apiClient.post('/ai/enhance/weekly/', data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  enhanceGeneralReport: async (data: EnhanceGeneralData): Promise<EnhanceTextResponse> => {
    try {
      const response = await apiClient.post('/ai/enhance/general/', data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  generateSummary: async (data: GenerateSummaryData): Promise<GenerateSummaryResponse> => {
    try {
      const response = await apiClient.post('/ai/generate/summary/', data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  suggestImprovements: async (data: SuggestImprovementsData): Promise<any> => {
    try {
      const response = await apiClient.post('/ai/suggest/improvements/', data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  getAIUsageStats: async (): Promise<AIUsageStats> => {
    try {
      const response = await apiClient.get('/ai/usage/');
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },
};

// Feedback Service
export const feedbackService = {
  submitFeedback: async (data: { type: string; subject: string; message: string }): Promise<{ success?: boolean; id?: number }> => {
    try {
      // Backend route lives under user app: api/auth/feedback/
      const response = await apiClient.post('/auth/feedback/', data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },
};

// Export Services
export const exportService = {
  exportWeeklyPDF: async (id: number): Promise<Blob> => {
    try {
      const response = await apiClient.get(`/export/weekly/${id}/pdf/`, {
        responseType: 'blob',
        timeout: EXPORT_REQUEST_TIMEOUT,
      });
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  exportWeeklyDOCX: async (id: number): Promise<Blob> => {
    try {
      const response = await apiClient.get(`/export/weekly/${id}/docx/`, {
        responseType: 'blob',
        timeout: EXPORT_REQUEST_TIMEOUT,
      });
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  exportDailyPDF: async (id: number): Promise<Blob> => {
    try {
      const response = await apiClient.get(`/export/daily/${id}/pdf/`, {
        responseType: 'blob',
        timeout: EXPORT_REQUEST_TIMEOUT,
      });
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  exportDailyDOCX: async (id: number): Promise<Blob> => {
    try {
      const response = await apiClient.get(`/export/daily/${id}/docx/`, {
        responseType: 'blob',
        timeout: EXPORT_REQUEST_TIMEOUT,
      });
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  exportGeneralPDF: async (): Promise<Blob> => {
    try {
      const response = await apiClient.get('/export/general/pdf/', {
        responseType: 'blob',
        timeout: EXPORT_REQUEST_TIMEOUT,
      });
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  exportGeneralDOCX: async (): Promise<Blob> => {
    try {
      const response = await apiClient.get('/export/general/docx/', {
        responseType: 'blob',
        timeout: EXPORT_REQUEST_TIMEOUT,
      });
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  bulkExport: async (data: BulkExportData): Promise<Blob> => {
    try {
      const response = await apiClient.post('/export/bulk/', data, {
        responseType: 'blob',
        timeout: EXPORT_REQUEST_TIMEOUT,
      });
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },
};

// Main Job Service
export const mainJobService = {
  getMainJobs: async (): Promise<MainJob[]> => {
    try {
      const response = await apiClient.get('/reports/main-jobs/');
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  getMainJob: async (id: number): Promise<MainJob> => {
    try {
      const response = await apiClient.get(`/reports/main-jobs/${id}/`);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  createMainJob: async (data: CreateMainJobData): Promise<MainJob> => {
    try {
      const response = await apiClient.post('/reports/main-jobs/', data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  updateMainJob: async (id: number, data: Partial<CreateMainJobData>): Promise<MainJob> => {
    try {
      const response = await apiClient.put(`/reports/main-jobs/${id}/`, data);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  deleteMainJob: async (id: number): Promise<void> => {
    try {
      await apiClient.delete(`/reports/main-jobs/${id}/`);
    } catch (error) {
      handleApiError(error as AxiosError);
      throw error;
    }
  },

  getOperations: async (mainJobId: number): Promise<MainJobOperation[]> => {
    try {
      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      try {
      const response = await apiClient.get(`/reports/main-jobs/${mainJobId}/operations/`, {
          signal: controller.signal
      });
        clearTimeout(timeoutId);
      return response.data.results || response.data;
      } catch (error: any) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          throw new Error('Request timeout - operations endpoint took too long to respond');
        }
        throw error;
      }
    } catch (error) {
      console.error('Failed to fetch operations for main job', mainJobId, ':', error);
      
      // Provide more specific error information
      if (error instanceof Error) {
        if (error.message.includes('timeout')) {
          throw new Error(`Operations fetch timed out for main job ${mainJobId}`);
        } else if (error.message.includes('500')) {
          throw new Error(`Server error (500) when fetching operations for main job ${mainJobId}`);
        } else if (error.message.includes('404')) {
          throw new Error(`Operations not found for main job ${mainJobId}`);
        }
      }
      
      throw error;
    }
  },

  createOperation: async (mainJobId: number, data: CreateMainJobOperationData): Promise<MainJobOperation> => {
    try {
      const response = await apiClient.post(`/reports/main-jobs/${mainJobId}/operations/`, data);
      return response.data;
    } catch (error) {
      console.error('Failed to create operation:', error);
      throw error;
    }
  },

  updateOperation: async (mainJobId: number, operationId: number, data: Partial<CreateMainJobOperationData>): Promise<MainJobOperation> => {
    try {
      const response = await apiClient.put(`/reports/main-jobs/${mainJobId}/operations/${operationId}/`, data);
      return response.data;
    } catch (error) {
      console.error('Failed to update operation:', error);
      throw error;
    }
  },

  deleteOperation: async (mainJobId: number, operationId: number): Promise<void> => {
    try {
      await apiClient.delete(`/reports/main-jobs/${mainJobId}/operations/${operationId}/`);
    } catch (error) {
      console.error('Failed to delete operation:', error);
      throw error;
    }
  },
};

// Reminder Services
export const reminderService = {
  // Get user's reminder settings
  getReminderSettings: async () => {
    const response = await apiClient.get('/reminders/settings/');
    return response.data;
  },

  // Update user's reminder settings
  updateReminderSettings: async (settings: any) => {
    const response = await apiClient.put('/reminders/settings/', settings);
    return response.data;
  },

  // Get scheduled notifications
  getScheduledNotifications: async () => {
    const response = await apiClient.get('/reminders/notifications/');
    return response.data;
  },

  // Create a new notification
  createNotification: async (notification: any) => {
    const response = await apiClient.post('/reminders/notifications/', notification);
    return response.data;
  },

  // Mark notification as sent
  markNotificationSent: async (notificationId: string) => {
    const response = await apiClient.patch(`/reminders/notifications/${notificationId}/`, {
      is_sent: true
    });
    return response.data;
  },

  // Test notification (for immediate testing)
  testNotification: async () => {
    const response = await apiClient.post('/reminders/test/');
    return response.data;
  }
};

// Billing Services
export const billingService = {
  // Get user balance
  getBalance: async (): Promise<{ success: boolean; data: UserBalance }> => {
    try {
      // Try the correct endpoint structure
      const response = await apiClient.get('/billing/balance/my_balance/');
      return response.data;
    } catch (error) {
      console.error('Failed to get balance:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Create transaction
  createTransaction: async (data: CreateTransactionData): Promise<{ success: boolean; message: string; data: Transaction }> => {
    try {
      // Use the correct endpoint structure
      const response = await apiClient.post('/billing/transactions/', data);
      return response.data;
    } catch (error) {
      console.error('Failed to create transaction:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Verify payment details
  verifyPayment: async (transactionId: number, data: { user_phone_number: string; sender_name: string; amount: number }): Promise<{ success: boolean; message: string }> => {
    try {
      // Use the correct endpoint structure
      const response = await apiClient.post(`/billing/transactions/${transactionId}/verify_payment/`, data);
      return response.data;
    } catch (error) {
      console.error('Failed to verify payment:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Get usage history
  getUsageHistory: async (): Promise<{ success: boolean; data: TokenUsage[] }> => {
    try {
      // Use the correct endpoint structure
      const response = await apiClient.get('/billing/token-usage/usage_history/');
      return response.data;
    } catch (error) {
      console.error('Failed to get usage history:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Get billing dashboard data
  getDashboardData: async (): Promise<{ success: boolean; data: BillingDashboardData }> => {
    try {
      // Use the correct endpoint structure
      const response = await apiClient.get('/billing/dashboard/dashboard_data/');
      return response.data;
    } catch (error) {
      console.error('Failed to get dashboard data:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Get payment information
  getPaymentInfo: async (): Promise<{ success: boolean; data: PaymentInfo }> => {
    try {
      // Use the correct endpoint structure
      const response = await apiClient.get('/billing/dashboard/payment_info/');
      return response.data;
    } catch (error) {
      console.error('Failed to get payment info:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Get user transactions
  getTransactions: async (): Promise<{ success: boolean; data: Transaction[] }> => {
    try {
      // Use the correct endpoint structure
      const response = await apiClient.get('/billing/transactions/');
      const payload = response.data;
      // Backend returns { success, data: [...] }
      if (payload && Array.isArray(payload.data)) {
        return { success: true, data: payload.data };
      }
      // Backend returns a raw array
      if (Array.isArray(payload)) {
        return { success: true, data: payload };
      }
      // Backend returns a paginated { count, next, previous, results: [...] }
      if (payload && Array.isArray(payload.results)) {
        return { success: true, data: payload.results };
      }
      return { success: false, data: [] };
    } catch (error) {
      console.error('Failed to get transactions:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Get user transactions (specific endpoint for authenticated user)
  getUserTransactions: async (): Promise<{ success: boolean; data: Transaction[] }> => {
    try {
      const response = await apiClient.get('/billing/transactions/user_transactions/');
      const payload = response.data;
      if (payload && Array.isArray(payload.data)) {
        return { success: true, data: payload.data };
      }
      if (Array.isArray(payload)) {
        return { success: true, data: payload };
      }
      if (payload && Array.isArray(payload.results)) {
        return { success: true, data: payload.results };
      }
      return { success: false, data: [] };
    } catch (error) {
      console.error('Failed to get user transactions:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Staff Transaction Management
  staff: {
    // Create transaction on behalf of user
    createTransaction: async (data: {
      user_id: number;
      user_phone_number: string;
      sender_name: string;
      payment_method: 'DIRECT' | 'WAKALA';
      wakala_name?: string;
      amount: number;
    }): Promise<{ success: boolean; message: string; data: Transaction }> => {
      try {
        const response = await apiClient.post('/billing/staff/transactions/', data);
        return response.data;
      } catch (error) {
        console.error('Failed to create staff transaction:', error);
        throw handleApiError(error as AxiosError);
      }
    },

    // Get pending transactions
    getPendingTransactions: async (): Promise<{ success: boolean; data: Transaction[] }> => {
      try {
        const response = await apiClient.get('/billing/staff/transactions/pending_transactions/');
        return response.data;
      } catch (error) {
        console.error('Failed to get pending transactions:', error);
        throw handleApiError(error as AxiosError);
      }
    },

    // Approve transaction
    approveTransaction: async (transactionId: number, note?: string): Promise<{ success: boolean; message: string; data?: Transaction }> => {
      try {
        const response = await apiClient.post(`/billing/staff/transactions/${transactionId}/approve_transaction/`, { note: note || '' });
        return response.data;
      } catch (error) {
        console.error('Failed to approve transaction:', error);
        throw handleApiError(error as AxiosError);
      }
    },

    // Reject transaction
    rejectTransaction: async (transactionId: number, note: string): Promise<{ success: boolean; message: string; data?: Transaction }> => {
      try {
        const response = await apiClient.post(`/billing/staff/transactions/${transactionId}/reject_transaction/`, { note });
        return response.data;
      } catch (error) {
        console.error('Failed to reject transaction:', error);
        throw handleApiError(error as AxiosError);
      }
    },

    // Get all transactions (staff view)
    getAllTransactions: async (params?: {
      status?: 'PENDING' | 'APPROVED' | 'REJECTED';
      payment_method?: 'DIRECT' | 'WAKALA';
      page?: number;
    }): Promise<{ success: boolean; data: Transaction[]; pagination?: any }> => {
      try {
        const response = await apiClient.get('/billing/staff/transactions/', { params });
        return response.data;
      } catch (error) {
        console.error('Failed to get all transactions:', error);
        throw handleApiError(error as AxiosError);
      }
    }
  }
};

// Notification Services
export const notificationService = {
  // Get current user's notifications (newest first)
  getNotifications: async (): Promise<{ success: boolean; data: Notification[] }> => {
    try {
      const response = await apiClient.get('/billing/notifications/');
      if (response.data && Array.isArray(response.data)) return { success: true, data: response.data };
      return response.data;
    } catch (error) {
      console.error('Failed to get notifications:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Get unread notifications count
  getUnreadCount: async (): Promise<{ success: boolean; data: { count: number } }> => {
    try {
      const response = await apiClient.get('/billing/notifications/unread_count/');
      return response.data;
    } catch (error) {
      console.error('Failed to get unread count:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Mark a notification as read
  markRead: async (notificationId: number): Promise<{ success: boolean; data: Notification }> => {
    try {
      const response = await apiClient.post(`/billing/notifications/${notificationId}/mark_read/`);
      return response.data;
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Mark all notifications as read
  markAllRead: async (): Promise<{ success: boolean; data: { updated: number } }> => {
    try {
      const response = await apiClient.post('/billing/notifications/mark_all_read/');
      return response.data;
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      throw handleApiError(error as AxiosError);
    }
  },
};

// Diagram Services
// Every diagram call is proxied by Django to the external media server
// (PythonAnywhere), which cold-starts slowly. Budget well above the media
// client's own 30s timeout so the frontend never aborts while Django is still
// uploading - that produced a false "failed" toast on a request that actually
// succeeded.
const DIAGRAM_REQUEST_TIMEOUT = 90000;

export const diagramService = {
  // Get diagram content for a weekly report
  getDiagram: async (weeklyReportId: number): Promise<{ success: boolean; data: any }> => {
    try {
      const response = await apiClient.get(`/export/diagram/${weeklyReportId}/`, {
        timeout: DIAGRAM_REQUEST_TIMEOUT
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch diagram:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Create diagram content for a weekly report
  createDiagram: async (weeklyReportId: number, text: string): Promise<{ success: boolean; data: any }> => {
    try {
      const response = await apiClient.post(`/export/diagram/${weeklyReportId}/`, { text }, {
        timeout: DIAGRAM_REQUEST_TIMEOUT
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create diagram:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Update diagram text for a weekly report
  updateDiagramText: async (weeklyReportId: number, text: string): Promise<{ success: boolean; data: any }> => {
    try {
      const response = await apiClient.put(`/export/diagram/${weeklyReportId}/`, { text }, {
        timeout: DIAGRAM_REQUEST_TIMEOUT
      });
      return response.data;
    } catch (error) {
      console.error('Failed to update diagram text:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Delete diagram content for a weekly report
  deleteDiagram: async (weeklyReportId: number): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.delete(`/export/diagram/${weeklyReportId}/`, {
        timeout: DIAGRAM_REQUEST_TIMEOUT
      });
      return response.data;
    } catch (error) {
      console.error('Failed to delete diagram:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Upload diagram image for a weekly report
  uploadDiagramImage: async (weeklyReportId: number, imageFile: File): Promise<{ success: boolean; data: any }> => {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);

      // Use a direct axios call with minimal headers so the browser sets the
      // multipart boundary. apiClient injects Content-Type: application/json
      // as a default, which breaks FormData uploads (backend returns 415).
      const token = localStorage.getItem('access_token');
      const baseURL = apiClient.defaults.baseURL;
      const response = await axios.post(`${baseURL}/export/diagram/${weeklyReportId}/image/`, formData, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : undefined,
          // Let browser set Content-Type with proper boundary
        },
        timeout: DIAGRAM_REQUEST_TIMEOUT,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to upload diagram image:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Delete diagram image for a weekly report
  deleteDiagramImage: async (weeklyReportId: number, imageId?: string): Promise<{ success: boolean; data: any }> => {
    try {
      const url = imageId 
        ? `/export/diagram/${weeklyReportId}/image/?image_id=${imageId}`
        : `/export/diagram/${weeklyReportId}/image/`;
      
      const response = await apiClient.delete(url, {
        timeout: DIAGRAM_REQUEST_TIMEOUT
      });
      return response.data;
    } catch (error) {
      console.error('Failed to delete diagram image:', error);
      throw handleApiError(error as AxiosError);
    }
  },
};

// Resources and Reports Services
export const resourcesService = {
  // Get all reports with filtering
  getReports: async (filters: {
    department?: string;
    report_type?: string;
    search?: string;
  } = {}): Promise<{ success: boolean; data: ReportResource[] }> => {
    try {
      const params = new URLSearchParams();
      if (filters.department) params.append('department', filters.department);
      if (filters.report_type) params.append('report_type', filters.report_type);
      if (filters.search) params.append('search', filters.search);

      const response = await apiClient.get(`/reports/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Upload a new report
  uploadReport: async (data: FormData): Promise<{ success: boolean; message: string; data?: ReportResource }> => {
    try {
      const response = await apiClient.post('/upload/', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to upload report:', error);
      throw handleApiError(error as AxiosError);
    }
  },

  // Download a report
  downloadReport: async (documentUrl: string, filename: string): Promise<void> => {
    try {
      const response = await apiClient.get(documentUrl, {
        responseType: 'blob',
        timeout: EXPORT_REQUEST_TIMEOUT,
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download report:', error);
      throw handleApiError(error as AxiosError);
    }
  }
};